import { isAllTimeSentinel } from "@utils/utils";
import ApexCharts from "apexcharts";
import React from "react";
import { twMerge } from "tailwind-merge";

import { CalendarDateRange, SkeletonLoader } from "@fronterahealth/frontera-ui-components";

import { CumulativeTargets } from "@api/graphql/types-and-hooks";
import { CumulativeTargetsChart, ZeroLineRange } from "@components/Charts/CumulativeSocialInteractionChart";
import {
  ChartSeries,
  EventAnnotation,
  PhaseAnnotation,
  SocialInteractionChart,
} from "@components/Charts/SocialInteractionChart";
import { useAdminData } from "@providers/AdminDataProvider";

interface GoalChartViewProps {
  /** False while session-progress is still loading and we don't yet know whether the goal is mixed. */
  detectionReady: boolean;
  effectiveChartIsCumulative: boolean;
  /** Apex chart id (must match approval-capture id format). */
  chartId: string;
  cumulativeTargets: CumulativeTargets[];
  /** Range of the flat line at 0 the Cumulative chart draws when nothing is mastered; unset keeps "No data yet". */
  cumulativeZeroLineRange?: ZeroLineRange;
  isCumulativeLoading: boolean;
  isLoading: boolean;
  series: ChartSeries[];
  phaseAnnotations: PhaseAnnotation[];
  eventAnnotations: EventAnnotation[];
  /** Undefined when the target declares no measurement type; the chart falls back to its generic axis. */
  progressType?: string;
  forClientProgramming: boolean;
  forReviewPage: boolean;
  chartRef?: React.RefObject<ApexCharts | null>;
  filterSessionDates?: CalendarDateRange;
}

const xAxisStartTimestamp = (filterSessionDates?: CalendarDateRange): number | undefined => {
  const start = filterSessionDates?.startDate;
  if (!start || isAllTimeSentinel(start)) return undefined;
  return new Date(start).getTime();
};

// The "All Time" preset's end bound is `new Date()` (today), not a sentinel like its epoch-0
// start -- so without this check the axis would extend to today instead of falling back to
// the actual data range (SocialInteractionChart's own xMax ?? Math.max(...allXValues)).
const xAxisEndTimestamp = (filterSessionDates?: CalendarDateRange): number | undefined => {
  const start = filterSessionDates?.startDate;
  const end = filterSessionDates?.endDate;
  if (start && isAllTimeSentinel(start)) return undefined;
  return end ? new Date(end).getTime() : undefined;
};

export const GoalChartView: React.FC<GoalChartViewProps> = ({
  detectionReady,
  effectiveChartIsCumulative,
  chartId,
  cumulativeTargets,
  cumulativeZeroLineRange,
  isCumulativeLoading,
  isLoading,
  series,
  phaseAnnotations,
  eventAnnotations,
  progressType,
  forClientProgramming,
  forReviewPage,
  chartRef,
  filterSessionDates,
}) => {
  const { isCentralReachUser } = useAdminData();

  if (!detectionReady) {
    // Render one unified skeleton while mixed-type detection resolves so we don't briefly
    // mount the wrong chart shape and double-flash skeletons (PROA-1511).
    return (
      <div className="flex justify-center items-center bg-white">
        <SkeletonLoader loading>
          <div className={twMerge("h-80", forClientProgramming ? "w-[55vw]" : "w-screen", "mx-16")}></div>
        </SkeletonLoader>
      </div>
    );
  }

  if (effectiveChartIsCumulative) {
    return (
      <CumulativeTargetsChart
        cumulativeTargets={cumulativeTargets}
        zeroLineRange={cumulativeZeroLineRange}
        chartId={chartId}
        isLoading={isCumulativeLoading}
        forClientProgramming={forClientProgramming || forReviewPage}
        chartRef={chartRef}
      />
    );
  }

  return (
    <SocialInteractionChart
      isLoading={isLoading}
      series={series}
      phaseAnnotations={phaseAnnotations}
      eventAnnotations={eventAnnotations}
      progressType={progressType}
      chartId={chartId}
      forClientProgramming={forClientProgramming || forReviewPage}
      chartRef={chartRef}
      xAxisMin={xAxisStartTimestamp(filterSessionDates)}
      xAxisMax={xAxisEndTimestamp(filterSessionDates)}
      breakAtPhaseChange={isCentralReachUser}
    />
  );
};
